import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";
import { LEVEL_STYLES } from "@/lib/enums";
import { Map as MapIcon, AlertTriangle, ArrowRight, LayoutGrid, Globe2 } from "lucide-react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { t } from "@/lib/i18n";

const levelForAvg = (v) =>
  v < 2 ? "below-action" : v < 4 ? "consider" : v < 8 ? "action" : v < 20 ? "high" : "very-high";

const CommunityMap = () => {
  const [data, setData] = useState(null);
  const [geo, setGeo] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("map"); // 'map' | 'grid'

  useEffect(() => {
    axios.get(`${API}/community-map`).then(
      (r) => setData(r.data),
      (e) => setErr(e.response?.data?.detail || "Failed to load community data."),
    );
    axios.get(`${API}/community-map/geo`).then((r) => setGeo(r.data)).catch(() => {});
  }, []);

  if (err) return <div className="p-12 text-center text-[#9B2C2C]">{err}</div>;
  if (!data) return <div className="p-12 eyebrow text-center">{t("Loading community readings…")}</div>;

  const zipsWithCoords = (geo?.zips || []).filter((z) => z.lat != null && z.lng != null);
  const center = zipsWithCoords.length
    ? [
        zipsWithCoords.reduce((a, z) => a + z.lat, 0) / zipsWithCoords.length,
        zipsWithCoords.reduce((a, z) => a + z.lng, 0) / zipsWithCoords.length,
      ]
    : [39.5, -98.35]; // continental US center

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 space-y-8" data-testid="community-map">
      <div className="fade-up flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="eyebrow">{t("Community Radon Map")}</p>
          <h1 className="font-serif text-4xl sm:text-5xl mt-2 text-[#1C201F]">
            {t("Aggregated readings by ZIP.")}
          </h1>
          <p className="text-[#5C6661] mt-3 max-w-3xl leading-relaxed">
            {t("Every dot represents a homeowner-submitted radon reading. Color reflects the average pCi/L for that ZIP.")}
          </p>
        </div>
        <div className="inline-flex rounded-full border border-[#E8ECEA] p-1 bg-white" data-testid="community-map-mode">
          <button
            onClick={() => setMode("map")}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
              mode === "map" ? "bg-[#2F524A] text-white" : "text-[#5C6661]"
            }`}
            data-testid="community-mode-map"
          >
            <Globe2 size={13} />{" "}{t("Map")}
          </button>
          <button
            onClick={() => setMode("grid")}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
              mode === "grid" ? "bg-[#2F524A] text-white" : "text-[#5C6661]"
            }`}
            data-testid="community-mode-grid"
          >
            <LayoutGrid size={13} />{" "}{t("Grid")}
          </button>
        </div>
      </div>

      <div className="card-flat fade-up delay-1 flex items-start gap-3 !bg-[#FCF3DC] !border-[#D6A73B]" data-testid="community-map-disclaimer">
        <AlertTriangle size={18} className="text-[#7A5A0F] mt-0.5" />
        <p className="text-sm text-[#7A5A0F] leading-relaxed">
          <strong>{t("Community data does not predict the radon level of any single home.")}</strong>{" "}
          {data.disclaimer.replace("Community data does not predict the radon level of an individual home. ", "")}
          {" "}
          <Link to="/signup" className="font-semibold underline">
            {t("Log your own reading to know for sure →")}
          </Link>
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 fade-up delay-2">
        <div className="card-flat"><p className="eyebrow">{t("Readings submitted")}</p><p className="font-serif text-4xl mt-2">{data.totals.readings}</p></div>
        <div className="card-flat"><p className="eyebrow">{t("ZIPs represented")}</p><p className="font-serif text-4xl mt-2">{data.totals.zips}</p></div>
        <div className="card-flat"><p className="eyebrow">{t("Highest reading")}</p><p className="font-serif text-4xl mt-2">{Math.max(0, ...data.zips.map((z) => z.max_pci || 0)).toFixed(1)}</p><p className="text-xs text-[#5C6661]">{t("pCi/L")}</p></div>
        <div className="card-flat"><p className="eyebrow">{t("Total ≥ 4 pCi/L")}</p><p className="font-serif text-4xl mt-2">{data.zips.reduce((a, z) => a + z.over_action, 0)}</p></div>
      </div>

      {mode === "map" ? (
        <div className="card-flat fade-up delay-3 !p-0 overflow-hidden" data-testid="community-map-leaflet" style={{ height: 480 }}>
          <MapContainer center={center} zoom={4} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">{t("OpenStreetMap")}</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {zipsWithCoords.map((z) => {
              const key = levelForAvg(z.avg_pci);
              const s = LEVEL_STYLES[key];
              return (
                <CircleMarker
                  key={`${z.state}-${z.zip}`}
                  center={[z.lat, z.lng]}
                  radius={Math.max(6, Math.min(20, 4 + z.readings * 2))}
                  pathOptions={{ color: s.border, fillColor: s.border, fillOpacity: 0.65 }}
                >
                  <Popup>
                    <strong>{z.city || z.zip}, {z.state}</strong><br />
                    {t("ZIP")}{" "}{z.zip} · {z.readings} reading{z.readings === 1 ? "" : "s"}<br />
                    {t("Avg:")}{" "}<strong>{z.avg_pci.toFixed(1)} pCi/L</strong><br />
                    {t("Over 4.0:")}{" "}{z.over_action}
                  </Popup>
                </CircleMarker>
              );
            })}
          </MapContainer>
        </div>
      ) : (
        <div className="card-flat fade-up delay-3" data-testid="community-map-grid">
          <h3 className="font-serif text-xl">{t("ZIP heat map")}</h3>
          <p className="text-xs text-[#5C6661] mt-1">
            {t("Legend:")}
            {[
              ["below-action", "< 2"],["consider", "2–3.9"],["action", "4–7.9"],["high", "8–19.9"],["very-high", "20+"],
            ].map(([k, l]) => (
              <span key={k} className="ml-3 inline-flex items-center gap-1 align-middle">
                <span className="inline-block w-3 h-3 rounded" style={{ background: LEVEL_STYLES[k].border }} />
                {l}
              </span>
            ))}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mt-4">
            {data.zips.map((z) => {
              const key = levelForAvg(z.avg_pci);
              const s = LEVEL_STYLES[key];
              return (
                <div key={`${z.state}-${z.zip}`}
                  className="rounded-2xl p-4 border transition-transform hover:-translate-y-0.5"
                  style={{ background: s.bg, borderColor: s.border }}
                  data-testid="community-zip-tile" data-zip={z.zip}>
                  <p className="text-xs text-[#5C6661]">{z.state}</p>
                  <p className="font-serif text-2xl text-[#1C201F]">{z.zip}</p>
                  <p className="mt-2 text-sm text-[#1C201F]">
                    <span className="font-serif text-lg">{z.avg_pci.toFixed(1)}</span>
                    <span className="text-xs text-[#5C6661]">{" "}{t("pCi/L avg")}</span>
                  </p>
                  <p className="text-[10px] text-[#5C6661] mt-1">
                    {z.readings} reading{z.readings === 1 ? "" : "s"} · {z.over_action} ≥ 4
                  </p>
                </div>
              );
            })}
            {data.zips.length === 0 && (
              <div className="col-span-full text-center text-[#5C6661] py-8">
                {t("No community readings yet. Be the first to add one.")}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card-flat fade-up delay-4 flex items-center justify-between gap-6 !p-8">
        <div>
          <p className="eyebrow">{t("Add your reading")}</p>
          <h3 className="font-serif text-2xl mt-2">{t("Help build the community picture.")}</h3>
        </div>
        <Link to="/signup" className="btn-primary inline-flex items-center gap-2">
          {t("Enter a reading")}{" "}<ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
};

export default CommunityMap;
